import classNames from 'classnames'
import type { ButtonHTMLAttributes, ReactNode, CSSProperties } from 'react'

interface Props extends ButtonHTMLAttributes<HTMLButtonElement> {
  bgColor?: string
  bgColorHover?: string
  bgColorDone?: string
  textColor?: string
  borderColor?: string
  isDone?: boolean
  loading?: boolean
  children: ReactNode
  fullWidth?: boolean
}

export default function Button({
  type = 'button',
  bgColor,
  bgColorHover,
  bgColorDone,
  textColor,
  borderColor,
  isDone,
  loading,
  disabled,
  fullWidth = true,
  children,
  className,
  ...rest
}: Props) {
  // Expliciete color-props overschrijven de globale --ws-* tenant-tokens
  // (nodig voor o.a. de rode "mislukt"-state op add-to-cart).
  const activeBg = isDone ? (bgColorDone ?? bgColor ?? 'var(--ws-btn-bg-done)') : (bgColor ?? 'var(--ws-btn-bg)')

  const cssVars = {
    '--btn-bg': activeBg,
    '--btn-bg-hover': bgColorHover ?? (isDone ? activeBg : 'var(--ws-btn-bg-hover)'),
    '--btn-text': textColor ?? 'var(--ws-btn-text)',
    '--btn-border': borderColor ?? 'transparent'
  } as CSSProperties

  return (
    <button
      type={type}
      disabled={disabled}
      aria-busy={loading || undefined}
      className={classNames(
        'ease-soft inline-flex cursor-pointer items-center justify-center gap-2 rounded-(--ws-radius-inner) px-4 py-2.5 text-center font-medium transition duration-200',
        fullWidth && 'w-full',
        'bg-[var(--btn-bg)] text-[var(--btn-text)] hover:bg-[var(--btn-bg-hover)]',
        'border border-[var(--btn-border)]',
        'shadow-xs hover:shadow-sm active:scale-[0.98]',
        'disabled:cursor-not-allowed disabled:opacity-60 disabled:active:scale-100',
        className
      )}
      style={cssVars}
      {...rest}>
      {loading && <span className="ws-spinner" aria-hidden />}
      {children}
    </button>
  )
}
